import { useEffect, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from '@dcp/shared'
import { Loader2, CalendarPlus } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import { useCreateProjection, useUpdateProjection, useCategories } from '@/hooks/useProjections'
import { useFacilities } from '@/hooks/useBanking'
import { Modal, Button, Input, Label, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, cn } from '@atrvd/saas-ui'
import type { ProjectionWithRelations, ProjectionStatus } from '@/lib/supabase'
import { nextCalendarDay } from '@/lib/date-utils'
import dayjs from '@dcp/shared/utils/dayjs'
import { AmountInput } from './AmountInput'

/**
 * Form validation schema for creating/editing projections
 */
/**
 * Base form schema — used as-is in EDIT mode.
 *
 * facility_id and description are intentionally optional here because both map to nullable DB
 * columns: facility_id is even actively nulled by ON DELETE SET NULL when a facility is deleted, and
 * description is null for imported projections. Requiring them in edit mode would block saving those
 * pre-existing, valid records (you couldn't change any unrelated field without first back-filling
 * them). They're mandatory only at creation time, via projectionCreateSchema below (DCP-255).
 *
 * category_id, date and amount are NOT NULL in the DB, so they stay required in both modes — an
 * existing row always has them, so the rule never blocks an edit.
 */
const projectionFormSchema = z.object({
  // `required_error` covers the untouched case (Select left unselected = undefined), `.min(1)`
  // covers an emptied text input ('') — both surface the same message.
  category_id: z
    .string({ required_error: 'Category is required' })
    .min(1, 'Category is required'),
  facility_id: z.string().optional(),
  date: z
    .string({ required_error: 'Projected payment date is required' })
    .min(1, 'Projected payment date is required'),
  account_name: z.string().optional(),
  transaction_type: z.string().optional(),
  description: z.string().optional(),
  // Amount is mandatory in both modes (DCP-255). DCP-250 AC#4: a negative amount is valid (outflow),
  // so any finite number — including 0 and negatives — is accepted. z.number() rejects both an empty
  // value (undefined → required_error) and unparseable text (AmountInput emits NaN, which Zod treats
  // as invalid_type → DCP-224's "Enter a valid amount"), so no explicit refine is needed.
  amount: z.number({
    required_error: 'Amount is required',
    invalid_type_error: 'Enter a valid amount',
  }),
  priority: z.boolean().optional(),
  include_in_report: z.boolean().optional(),
  status: z.enum(['draft', 'confirmed', 'actual']).optional(),
  notes: z.string().optional(),
  attachment_url: z.string().url('Invalid URL format').optional().or(z.literal('')),
})

/**
 * Create-mode schema: a brand-new projection must name a facility and a description (DCP-255).
 * These two are tightened to required only here — edit mode uses the lenient base schema above so
 * existing rows with a null facility_id/description remain editable.
 */
const projectionCreateSchema = projectionFormSchema.extend({
  facility_id: z
    .string({ required_error: 'Facility is required' })
    .min(1, 'Facility is required'),
  description: z
    .string({ required_error: 'Description is required' })
    .min(1, 'Description is required'),
})

type ProjectionFormValues = z.infer<typeof projectionFormSchema>

/**
 * Props for ProjectionFormDialog component
 */
interface ProjectionFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projection?: ProjectionWithRelations
  /** Optional callback invoked with the ID of the newly created projection (create mode only) */
  onCreated?: (ids: string[]) => void
}

/**
 * Dialog form for creating and editing projections
 * Handles both create and edit modes based on presence of projection prop
 * Includes all fields from the UI mockup
 */
export function ProjectionFormDialog({ open, onOpenChange, projection, onCreated }: ProjectionFormDialogProps) {
  const createProjection = useCreateProjection()
  const updateProjection = useUpdateProjection()
  // Full list (incl. inactive): this dialog SEEDS `facility_id` from an existing
  // record (PR #183 review). With an active-only list a record pointing at a
  // since-deactivated facility renders the Select's placeholder — Radix shows it
  // when no SelectItem matches — while react-hook-form still holds the real id.
  // A user "fixing" the apparently-empty required field then silently reassigns
  // the record. The lists that reach this dialog are scoped elsewhere, so this
  // only covers the cross-tab window.
  const { data: facilitiesData } = useFacilities({ activeOnly: false })
  const facilities = facilitiesData ?? []
  const { data: categoriesData } = useCategories()
  const categories = categoriesData ?? []

  // Determine if this is edit mode
  const isEditMode = !!projection

  // Prepare default values for edit mode
  const defaultValues = projection ? {
    category_id: projection.category_id,
    facility_id: projection.facility_id || undefined,
    date: projection.date,
    account_name: projection.account_name || undefined,
    transaction_type: projection.transaction_type || undefined,
    description: projection.description || undefined,
    // DCP-250: single signed amount. `?? undefined` keeps a real 0 (a valid amount) and only
    // blanks a null/absent value; a truthy check would wrongly treat 0 as empty (DCP-255).
    amount: projection.amount ?? undefined,
    priority: projection.priority || false,
    include_in_report: projection.include_in_report ?? true,
    status: projection.status as ProjectionStatus,
    notes: projection.notes || undefined,
    attachment_url: projection.attachment_url || undefined,
  } : {
    priority: false,
    include_in_report: true,
    status: 'draft' as ProjectionStatus,
  }

  // Validation differs by mode: create enforces facility_id + description (DCP-255), edit keeps them
  // optional so a record with a pre-existing null stays editable (see the schemas above).
  const resolver = useMemo(
    () => zodResolver(isEditMode ? projectionFormSchema : projectionCreateSchema),
    [isEditMode],
  )

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<ProjectionFormValues>({
    resolver,
    defaultValues,
  })

  // Reset form when projection changes (for edit mode)
  useEffect(() => {
    if (projection && open) {
      reset({
        category_id: projection.category_id,
        facility_id: projection.facility_id || undefined,
        date: projection.date,
        account_name: projection.account_name || undefined,
        transaction_type: projection.transaction_type || undefined,
        description: projection.description || undefined,
        // DCP-250: single signed amount. `?? undefined` keeps a real 0 (a valid amount) and only
        // blanks a null/absent value; a truthy check would wrongly treat 0 as empty (DCP-255).
        amount: projection.amount ?? undefined,
        priority: projection.priority || false,
        include_in_report: projection.include_in_report ?? true,
        status: projection.status as ProjectionStatus,
        notes: projection.notes || undefined,
        attachment_url: projection.attachment_url || undefined,
      })
    } else if (open && !projection) {
      reset({
        priority: false,
        include_in_report: true,
        status: 'draft' as ProjectionStatus,
      })
    }
  }, [projection, open, reset])

  // Current Projected Payment Date value — watched so the "Move to next day"
  // button can react to it on every change.
  const dateValue = watch('date')
  // Only enable the shift button for a real date, matching the codebase's
  // dayjs().isValid() convention — the (throwing) nextCalendarDay() helper must
  // never receive invalid input from this path.
  const isDateValid = !!dateValue && dayjs(dateValue).isValid()

  /**
   * Handle form submission
   * Creates or updates projection and shows success/error toast
   */
  const onSubmit = async (data: ProjectionFormValues) => {
    try {
      if (isEditMode) {
        await updateProjection.mutateAsync({
          id: projection.id,
          data: {
            category_id: data.category_id,
            // facility_id and description are optional in edit mode (nullable columns). Forward an
            // empty value as undefined so the update leaves a pre-existing null untouched instead of
            // failing validation — existing records stay editable (DCP-255).
            facility_id: data.facility_id || undefined,
            date: data.date,
            account_name: data.account_name || undefined,
            transaction_type: data.transaction_type || undefined,
            description: data.description || undefined,
            // DCP-250: single signed amount (positive=inflow, negative=outflow).
            // Required + NaN-refined by the schema, so data.amount is always a number (DCP-255).
            amount: data.amount,
            priority: data.priority ?? false,
            include_in_report: data.include_in_report ?? true,
            status: data.status || 'draft',
            notes: data.notes || undefined,
            attachment_url: data.attachment_url || undefined,
          },
        })
        toast.success('Projection updated')
      } else {
        // Capture result to notify parent of created projection ID
        const result = await createProjection.mutateAsync({
          category_id: data.category_id,
          // Required in create mode (enforced by projectionCreateSchema); `|| undefined` is a
          // harmless no-op there and keeps the mapping identical to the update path (DCP-255).
          facility_id: data.facility_id || undefined,
          date: data.date,
          // Optional free-text fields: coerce an emptied input ('') to undefined (→ null in the
          // DB), matching the update path above and the form's load/reset normalization (DCP-255).
          account_name: data.account_name || undefined,
          transaction_type: data.transaction_type || undefined,
          description: data.description || undefined,
          // DCP-250: single signed amount (positive=inflow, negative=outflow).
          // Required + NaN-refined by the schema, so data.amount is always a number (DCP-255).
          amount: data.amount,
          priority: data.priority ?? false,
          include_in_report: data.include_in_report ?? true,
          status: data.status,
          notes: data.notes || undefined,
          attachment_url: data.attachment_url || undefined,
        })
        if (onCreated && result) {
          onCreated([result.id])
        }
        toast.success('Projection created')
      }

      reset()
      onOpenChange(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save projection'
      toast.error(message)
    }
  }

  /**
   * Handle modal close - reset form on close
   */
  const handleClose = () => {
    reset()
    onOpenChange(false)
  }

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={isEditMode ? 'Edit Projection' : 'Create Projection'}
      description={isEditMode ? 'Update the projection details below.' : 'Create a new projection for cash flow forecasting.'}
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
          >
            Cancel
          </Button>
          {/* This footer button sits outside the <form> (Modal renders the footer separately), so
              type="submit" would be inert — submission is wired via the onClick handler instead.
              type="button" is the accurate semantic (DCP-255). */}
          <Button
            type="button"
            disabled={createProjection.isPending || updateProjection.isPending}
            onClick={handleSubmit(onSubmit)}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {(createProjection.isPending || updateProjection.isPending) ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {/* DCP-255: submit text tracks the form mode, mirroring the modal title above */}
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEditMode ? 'Update' : 'Create'
            )}
          </Button>
        </div>
      }
    >
      {/* No own max-height / overflow on this form (DCP-336): the saas-ui Modal
          body is already a scroll container (max-h-[60vh] overflow-y-auto), so a
          nested one produced a second, inner scrollbar — and the inner max-h-[70vh]
          could never be reached anyway, since the outer wrapper already caps the
          available height at 60vh. Letting the form flow makes the modal body the
          single scroller (same fix as DCP-312 / DCP-313). */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-1">
        <div className="grid grid-cols-2 gap-4">
          {/* Date */}
          <div className="space-y-2">
            <Label htmlFor="date">
              Projected payment date <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="date"
                type="date"
                className="flex-1"
                {...register('date')}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                aria-label="Move to next day"
                title="Move to next day"
                disabled={!isDateValid}
                onClick={() => setValue('date', nextCalendarDay(dateValue), { shouldValidate: true, shouldDirty: true })}
              >
                <CalendarPlus className="h-4 w-4" />
              </Button>
            </div>
            {errors.date && (
              <p className="text-sm text-destructive">{errors.date.message}</p>
            )}
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={watch('status') || 'draft'}
              onValueChange={(value) => setValue('status', value as ProjectionStatus)}
            >
              <SelectTrigger id="status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="actual">Actual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* -------------------------------------------------------------------
            DIALOG INVARIANT (DCP-337): the Category and Facility Selects below
            must ignore a value that is not one of their own rendered options.

            Radix keeps a hidden native <select> in sync with the controlled
            `value` but registers its <option> list from a child layout effect,
            so the two can disagree for a commit. When `value` moves to
            something the registered option set does not contain, the native
            select cannot take it, resets to '', and Radix dispatches that ''
            back through onValueChange indistinguishably from a real user pick.

            Reachable here because ProjectionsPage keeps this dialog
            permanently mounted and only flips `open` (no `key`): the seeding
            effect can set `category_id` / `facility_id` before
            `useCategories` / `useFacilities` have resolved, i.e. against an
            option list that is still empty. Both fields then showed their
            placeholder while the form held nothing, and Update failed on
            "Category is required" under a field the user never touched. No row
            was ever corrupted — the payload sends `|| undefined` and
            `projections.update` drops undefined keys — but the edit could not
            be saved, and re-picking a facility to "fix" the empty field does
            persist.

            The Status Select above needs no guard: its options are three
            hardcoded SelectItems, so they always exist at mount.

            Unfixed upstream: radix-ui/primitives#2817 (root), #3693 (against
            our 2.2.6), fix PR #3759 unmerged. Both guards go when that lands.
            Same defect and same treatment as ManualTransactionFormDialog.
            ------------------------------------------------------------------- */}

        {/* Transaction Category */}
        <div className="space-y-2">
          <Label htmlFor="category_id">
            Transaction category <span className="text-destructive">*</span>
          </Label>
          <Select
            // `?? ''` keeps the Select controlled from the first render; with
            // `undefined` Radix treats it as uncontrolled for that render and
            // React logs "changing from uncontrolled to controlled".
            value={watch('category_id') ?? ''}
            onValueChange={(value) => {
              // Dialog invariant (see above). No 'none' sentinel — the options
              // are real category ids only.
              if (!categories.some((c) => c.id === value)) return
              setValue('category_id', value)
            }}
          >
            <SelectTrigger id="category_id">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {/* DCP-250: flat category list — sign comes from the amount, not category type */}
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.category_id && (
            <p className="text-sm text-destructive">{errors.category_id.message}</p>
          )}
        </div>

        {/* Facility — mandatory: every projection must be tied to a facility */}
        <div className="space-y-2">
          <Label htmlFor="facility_id">
            Facility <span className="text-destructive">*</span>
          </Label>
          <Select
            // See the `?? ''` note on the Category Select above.
            value={watch('facility_id') ?? ''}
            onValueChange={(value) => {
              // Dialog invariant (see above). Options are real facility ids only.
              if (!facilities.some((f) => f.id === value)) return
              setValue('facility_id', value)
            }}
          >
            <SelectTrigger id="facility_id">
              <SelectValue placeholder="Select a facility" />
            </SelectTrigger>
            <SelectContent>
              {facilities.map((facility) => (
                <SelectItem key={facility.id} value={facility.id}>
                  {facility.name} {facility.code && `(${facility.code})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.facility_id && (
            <p className="text-sm text-destructive">{errors.facility_id.message}</p>
          )}
        </div>

        {/* Account Name and Transaction Type */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="account_name">Account Name</Label>
            <Input
              id="account_name"
              placeholder="e.g., Main Checking"
              {...register('account_name')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="transaction_type">Transaction Type</Label>
            <Input
              id="transaction_type"
              placeholder="e.g., Wire, ACH, Check"
              {...register('transaction_type')}
            />
          </div>
        </div>

        {/* Description — mandatory */}
        <div className="space-y-2">
          <Label htmlFor="description">
            Description <span className="text-destructive">*</span>
          </Label>
          <Input
            id="description"
            placeholder="Brief description of projection"
            {...register('description')}
          />
          {errors.description && (
            <p className="text-sm text-destructive">{errors.description.message}</p>
          )}
        </div>

        {/* Amount — DCP-224/225: locale-stable AmountInput instead of type="number" */}
        <div className="space-y-2">
          <Label htmlFor="amount">
            Amount <span className="text-destructive">*</span>
          </Label>
          {/* DCP-224/225: locale-stable AmountInput (type="text" + parseAmount) instead of
              type="number"; the DCP-255 mandatory marker on the label is retained. */}
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <AmountInput
                id="amount"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="e.g. 1000 (inflow) or -500 (outflow)"
              />
            )}
          />
          {/* DCP-250: sign now comes from the value — tell the user what it means. */}
          <p className="text-sm text-muted-foreground">
            Positive = inflow, negative = outflow.
          </p>
          {errors.amount && (
            <p className="text-sm text-destructive">{errors.amount.message}</p>
          )}
        </div>

        {/* Attachment */}
        <div className="space-y-2">
          <Label htmlFor="attachment_url">Attachment URL</Label>
          <Input
            id="attachment_url"
            placeholder="https://..."
            {...register('attachment_url')}
          />
          {/* attachment_url is URL-validated in the schema; surface the error inline like the
              other fields so an invalid URL is not a silent submit-block (DCP-255). */}
          {errors.attachment_url && (
            <p className="text-sm text-destructive">{errors.attachment_url.message}</p>
          )}
        </div>

        {/* Priority and Include in Report Checkboxes */}
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <input
              id="priority"
              type="checkbox"
              {...register('priority')}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="priority" className="text-sm font-normal cursor-pointer">
              Priority
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="include_in_report"
              type="checkbox"
              {...register('include_in_report')}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="include_in_report" className="text-sm font-normal cursor-pointer">
              Include in report
            </Label>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <textarea
            id="notes"
            placeholder="Add any additional notes..."
            rows={3}
            className={cn(
              'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
            )}
            {...register('notes')}
          />
        </div>
      </form>
    </Modal>
  )
}
